import { apiService } from "@/services";
import { useMutation, useQuery } from "@tanstack/react-query";
import { handleApiError } from "@/utils";
import queryClient from "@/query-client";
import type { BrandContextDocument } from "@/types/brand-context";
import { DEFAULT_STALE_TIME } from "@/constants";

const EXTRACTION_POLL_INTERVAL_MS = 4000;

export const brandContextDocumentsQueryKey = (brandId: number | null | undefined) =>
  ["brand", "brand-context-documents", brandId] as const;

export const useGetBrandContextDocuments = (brandId: number | null) =>
  useQuery<BrandContextDocument[]>({
    queryKey: brandContextDocumentsQueryKey(brandId),
    queryFn: () => apiService.get<BrandContextDocument[]>(`/brands/${brandId}/brand-context/documents`),
    enabled: !!brandId,
    staleTime: DEFAULT_STALE_TIME,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      const hasInFlight = data.some(
        (d) => d.extractionStatus === "pending" || d.extractionStatus === "processing",
      );
      return hasInFlight ? EXTRACTION_POLL_INTERVAL_MS : false;
    },
  });

export type UploadBrandContextDocumentInput = {
  brandId: number;
  file: File;
  title: string;
  category: string;
  tags?: string[];
};

export const useUploadBrandContextDocument = () =>
  useMutation<BrandContextDocument, unknown, UploadBrandContextDocumentInput>({
    mutationFn: ({ brandId, file, title, category, tags }) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", title);
      formData.append("category", category);
      (tags ?? []).forEach((tag) => formData.append("tags[]", tag));

      return apiService.post<BrandContextDocument, FormData>(
        `/brands/${brandId}/brand-context/documents`,
        formData,
      );
    },
    onSuccess: (_serverData, variables) => {
      queryClient.invalidateQueries({ queryKey: brandContextDocumentsQueryKey(variables.brandId) });
    },
    onError: (error) => handleApiError(error),
  });

export type UpdateBrandContextDocumentInput = {
  brandId: number;
  documentId: string;
  title: string;
  category: string;
  tags?: string[];
};

export const useUpdateBrandContextDocument = () =>
  useMutation<BrandContextDocument, unknown, UpdateBrandContextDocumentInput>({
    mutationFn: ({ brandId, documentId, title, category, tags }) =>
      apiService.patch<BrandContextDocument, { title: string; category: string; tags?: string[] }>(
        `/brands/${brandId}/brand-context/documents/${documentId}`,
        { title, category, tags },
      ),
    onSuccess: (_serverData, variables) => {
      queryClient.invalidateQueries({ queryKey: brandContextDocumentsQueryKey(variables.brandId) });
    },
    onError: (error) => handleApiError(error),
  });

export const useDeleteBrandContextDocument = () =>
  useMutation<unknown, unknown, { brandId: number; documentId: string }>({
    mutationFn: ({ brandId, documentId }) =>
      apiService.delete(`/brands/${brandId}/brand-context/documents/${documentId}`),
    onSuccess: (_serverData, variables) => {
      queryClient.invalidateQueries({ queryKey: brandContextDocumentsQueryKey(variables.brandId) });
    },
    onError: (error) => handleApiError(error),
  });

export const useDownloadBrandContextDocument = () =>
  useMutation<void, unknown, { brandId: number; documentId: string; fileName: string; mimeType: string }>({
    mutationFn: async ({ brandId, documentId, fileName, mimeType }) => {
      const response = await apiService._api({
        method: "get",
        url: `/brands/${brandId}/brand-context/documents/${documentId}/download`,
        responseType: "blob",
      });

      const blob = new Blob([response as BlobPart], { type: mimeType });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    onError: (error) => handleApiError(error),
  });
